import type { DirectoryPrivate } from '@repo/types/directory';
import type { SortingState } from '@tanstack/react-table';

import { cn } from '@repo/lib/cn';
import { DirectoryPath } from '@repo/types/directory';
import { useMemo } from 'react';

import FSPath from '@/components/features/files/FsPath';
import FsActionButtons from '@/components/features/files/layouts/header/FsActionButtons';
import FsDriveType from '@/components/features/files/layouts/header/FsDriveType';
import FsManageBar from '@/components/features/files/layouts/header/FsManageBar';
import { useFilesStorage } from '@/store/files-store';

export interface FsPageHeaderProps {
  handleNavigateTo: (directoryId?: string) => void;
  handleTabChange: (tab: string) => void;
  fsPageType: string;
  directory?: DirectoryPrivate;
  isLoading: boolean;
  isError: boolean;
  sorting: SortingState;
  directoryId?: string;
  onUploadFilesOpen: () => void;
  onUploadDirOpen: () => void;
}

export default function FsPageHeader(fsPageHeaderProps: FsPageHeaderProps) {
  const { directory, isError, isLoading, fsPageType, directoryId, handleNavigateTo, handleTabChange, onUploadFilesOpen, onUploadDirOpen } = fsPageHeaderProps;
  const { permissions, setOpenCreateDir, selectedSize } = useFilesStorage();

  const size: number = selectedSize();
  const isActionsDisabled: boolean = isError || isLoading || !permissions.write || directoryId === undefined;
  const isSelectionActive: boolean = size > 0;

  const handleOnCreateDir = () => {
    setOpenCreateDir(true);
  };

  const rootPath = useMemo<DirectoryPath | undefined>(() => {
    if (fsPageType === 'SHARED_DRIVE') {
      return {
        id: 'shared-drive-root',
        name: 'Udostępnione',
        parentDirId: null,
      };
    }
    return undefined;
  }, [fsPageType]);

  return (
    <header className={'border-border-primary bg-background grid w-full auto-rows-min grid-cols-2 gap-3 rounded-2xl border p-4'}>
      {/* Path navigation section */}
      <div className={'col-span-2 row-start-1 flex gap-0.5 md:col-span-1'}>
        <FSPath directory={directory} rootPath={rootPath} isLoading={isLoading} isError={isError} onRouteChange={handleNavigateTo} />
      </div>

      {/* Action buttons section */}
      <FsActionButtons
        onCreateDir={handleOnCreateDir}
        disabled={isActionsDisabled}
        onUploadFilesOpen={onUploadFilesOpen}
        onUploadDirOpen={onUploadDirOpen}
        className={cn('col-span-1 flex shrink-0 justify-end gap-2 md:col-start-2 md:row-start-1', isSelectionActive && 'hidden md:flex')}
      />

      {/* Selection management bar - only visible when selection is active */}
      <FsManageBar className={cn('col-span-2', isSelectionActive ? 'flex' : 'hidden')} />

      {/* Tabs section */}
      <FsDriveType value={fsPageType} onValueChange={handleTabChange} className={cn('col-span-1 row-start-2', isSelectionActive && 'hidden')} />
    </header>
  );
}
